/**
 * Opus operator schemas — the people who can sign in to Opus itself,
 * distinct from Aruba Central / Meraki dashboard users (which live under
 * /aruba/users etc).
 *
 * Mirrors Django's User + Profile + Role models from `app_register`.
 */
import { z } from "zod";

/** Built-in Opus roles — matches `auth.ROLE_TIER` on the api-shim
 *  (viewer=1, developer=2, admin=3). Anything else fails-closed to
 *  viewer tier, so we intentionally restrict the dropdown to the
 *  three real tiers — the legacy noc-l1/noc-l2/auditor/guest names
 *  were aspirational and didn't grant the permissions the labels
 *  implied. Don't reintroduce them without backing tier-mapping. */
const OpusRoleSchema = z.enum([
  "viewer",      // read-only across the estate; cannot propose writes
  "developer",   // reads + propose-then-confirm writes (allowlist-gated)
  "admin",       // everything, including high-impact tools (audit search,
                 // backup_restore, compliance_remediate, Aruba rename/move/assign)
]);
export type OpusRole = z.infer<typeof OpusRoleSchema>;

/**
 * Combined User + Profile shape returned by /api/users/.
 *
 * The backend joins Profile fields into the user row to keep the table
 * one-roundtrip-per-page. The full Profile is fetched lazily on click.
 */
const OpusUserSchema = z.object({
  id: z.number(),
  username: z.string(),
  email: z.string(),
  fullName: z.string(),
  /** Job title from Profile (e.g. "Senior Network Engineer"). Optional. */
  title: z.string().nullable().optional(),
  /** Country from Profile — used for an emoji flag in the table. */
  country: z.string().nullable().optional(),
  role: OpusRoleSchema,
  isStaff: z.boolean(),
  isActive: z.boolean(),
  /** Per-user AI agent kill-switch. Default true; admins can flip it
   *  off to revoke /api/ai/chat access without demoting the user's role. */
  agentEnabled: z.boolean().default(true),
  /** ISO timestamp; null if never signed in. */
  lastLoginAt: z.string().nullable().optional(),
  /** ISO timestamp the user was created. */
  createdAt: z.string(),
});
export type OpusUser = z.infer<typeof OpusUserSchema>;
export const OpusUserListSchema = z.array(OpusUserSchema);

/** Payload for POST /api/users/. */
export const OpusUserCreateSchema = z.object({
  username: z.string().min(1, "Required"),
  email: z.string().email("Invalid email"),
  fullName: z.string().min(1, "Required"),
  role: OpusRoleSchema,
  title: z.string().optional(),
});
export type OpusUserCreate = z.infer<typeof OpusUserCreateSchema>;
